import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSiteName } from "@/hooks/useSiteName";
import { ValidationError } from "@/lib/http-client";
import type { LayoutContext } from "@/lib/models";
import { setFormErrors } from "@/lib/react-form-utils";
import { featureTypeEditUrlBuilder } from "@/lib/url-builder";
import { Code, CodeService } from "@/services/code-service";
import { FeatureTypeService, type NewFeatureTypeRequest } from "@/services/FeatureTypeService";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useOutletContext, useParams } from "react-router";

const FeatureTypeCreatePage: React.FC = () => {
  const params = useParams();
  const { siteName } = useSiteName(params.siteId);
  const { register, handleSubmit, formState: { isDirty, errors }, setError } = useForm<NewFeatureTypeRequest>({defaultValues: {description: "", featureCodeId: "", siteId: params.siteId!}});
  const [featureCodes, setFeatureCodes] = useState<Code[]>([]);
  const { setPageName } = useOutletContext<LayoutContext>();
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setIsSaveDisabled(!isDirty);
  }, [isDirty]);

  useEffect(() => {
    const initializeData = async () => {
      if (!params.siteId) {
        return;
      }

      const codes = await CodeService.getCodes2();
      setFeatureCodes(codes.filter(c => c.type === 'Feature'));
    };
    initializeData();
  }, [params.siteId]);

  useEffect(() => {
    setPageName(`Site: ${siteName} Create Feature Type`);
  }, [siteName, setPageName]);

  const handleFormSubmit = async (formData: NewFeatureTypeRequest) => {
    if (!params.siteId) {
      alert('error invalid siteId')
      return null
    }

    try {
        const featureType = await FeatureTypeService.create(formData);
        navigate(featureTypeEditUrlBuilder(params.siteId, featureType.id));
    } catch (error) {
        if (error instanceof ValidationError) {
          setFormErrors<NewFeatureTypeRequest>(error.errors, setError, formData)
        }
    }
    return null;
  };

  return (
    <div className="container mx-auto p-4">
      <Header headerTitle={`Site: ${siteName}`} />
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <div>
          <Label
            htmlFor="description"
          >
            Description
          </Label>
          <Input
            type="text"
            {...register("description")}
          />
          <span className="text-sm text-red-500">{errors.description?.message}</span>
        </div>
        <div>
          <Label
            htmlFor="featureCodeId"
            className="block text-sm font-medium text-gray-700"
          >
            Feature Code *
          </Label>
          <select
            id="featureCodeId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("featureCodeId", { required: 'Feature code is required'})}
          >
            <option value="">Select feature</option>
            {featureCodes
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((featureCode) => (
                <option key={featureCode.id} value={featureCode.id!}>
                  {featureCode.name}
                </option>
              ))}
          </select>
          <span className="text-sm text-red-500">{errors.featureCodeId?.message}</span>
        </div>   
        <div className="flex justify-end space-x-2">
          <button
            type="reset"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Create Feature Type
          </Button>
        </div>
      </form>
    </div>
  );
};

export default FeatureTypeCreatePage;
